// apps/frontend/src/lib/pipeline-preview/transformers/source.ts
import { apiClient } from '@/lib/api-client';
import type { PipelineNode, ExecutionContext, TransformerResult } from '../types';
import { inferSchema } from '../utils/schema';

/**
 * SOURCE Transformer - Fetches data from data source endpoint
 * Uses cache to avoid duplicate API calls
 */
export async function sourceData(
  node: PipelineNode,
  _inputData: unknown[],
  context: ExecutionContext
): Promise<TransformerResult> {
  const startTime = performance.now();

  try {
    const config = node.data.config as any;
    const dataSourceId = config?.dataSourceId;

    if (!dataSourceId) {
      return {
        success: false,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: 0 },
        error: 'Missing data source ID',
      };
    }

    // Check cache first
    const cacheKey = `source:${dataSourceId}`;
    if (context.dataCache.has(cacheKey)) {
      const cachedData = context.dataCache.get(cacheKey) as unknown[];
      const endTime = performance.now();

      return {
        success: true,
        data: cachedData,
        schema: inferSchema(cachedData),
        stats: {
          rows: cachedData.length,
          executionTime: Math.round(endTime - startTime),
        },
      };
    }

    // Fetch from API
    const response = await apiClient.get<{
      data: unknown[];
      totalRows: number;
      jobId: string;
    }>(`/preview/data-sources/${dataSourceId}`);

    const data = response.data;

    // Cache the result
    context.dataCache.set(cacheKey, data);

    const endTime = performance.now();

    return {
      success: true,
      data,
      schema: inferSchema(data),
      stats: {
        rows: data.length,
        executionTime: Math.round(endTime - startTime),
      },
    };
  } catch (error: any) {
    const endTime = performance.now();
    return {
      success: false,
      data: [],
      schema: { columns: [] },
      stats: { rows: 0, executionTime: Math.round(endTime - startTime) },
      error: error.message || 'Failed to fetch source data',
    };
  }
}
